import React, { useState, useEffect } from 'react';
import { Copy, X, Link as LinkIcon, Check, Plus, Tag } from 'react-feather';
import { API_URL, fetchWithAuth } from '../../api';

const EventLinksTab = ({ eventId, isCreator }) => {
  // --- ESTADOS ---
  const [promoterLinks, setPromoterLinks] = useState([]);
  const [showPromoterLinkModal, setShowPromoterLinkModal] = useState(false);
  
  // MODIFICACIÓN: Estado inicial con name vacío
  const [newPromoterLink, setNewPromoterLink] = useState({ 
    max_uses: 10,
    target_id: 1
  });
  
  const [loading, setLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  
  const limit = 10;
  const [error, setError] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  const [team, setTeam] = useState([]);

  const [editingLink, setEditingLink] = useState(null); // Link que estamos editando
  const [editLoading, setEditLoading] = useState(false);

  const handleUpdateLink = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      await fetchWithAuth(`${API_URL}/promoter-links/${editingLink.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ max_uses: editingLink.max_uses })
      });
      setEditingLink(null);
      await fetchPromoterLinks();
    } catch (err) {
      setError('Error al actualizar el límite.');
    } finally {
      setEditLoading(false);
    }
  };

  // --- FUNCIONES ---
  const fetchPromoterLinks = async (currentPage = page) => {
    try {
      setLoading(true);
      const skip = currentPage * limit;
      const response = await fetchWithAuth(
        `${API_URL}/events/${eventId}/promoter-links?skip=${skip}&limit=${limit}`
      );
      setPromoterLinks(response.data || []);
      setTotal(response.total || 0);
    } catch (err) {
      setError('No se pudieron cargar los links.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePromoterLink = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    setError('');
    try {
      await fetchWithAuth(`${API_URL}/events/${eventId}/create-promoter-link`, {
        method: 'POST',
        body: JSON.stringify(newPromoterLink)
      });
      // MODIFICACIÓN: Reset con name vacío
      setNewPromoterLink({ max_uses: 10, name: '' });
      setShowPromoterLinkModal(false);
      await fetchPromoterLinks();
    } catch (err) {
      setError(typeof err.message === 'string' ? err.message : 'Error al crear link');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeletePromoterLink = async (promoterId) => {
    if (!window.confirm("¿Seguro que quieres eliminar este link?")) return;
    try {
      await fetchWithAuth(`${API_URL}/promoter-links/${promoterId}`, { method: 'DELETE' });
      await fetchPromoterLinks();
    } catch (err) {
      setError('Error al eliminar link');
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handlePrevPage = () => { if (page > 0) { setPage(p => p - 1); fetchPromoterLinks(page - 1); } };
  const handleNextPage = () => { if (page < Math.ceil(total / limit) - 1) { setPage(p => p + 1); fetchPromoterLinks(page + 1); } };

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const response = await fetchWithAuth(`${API_URL}/events/${eventId}/team-hierarchy`);
        setTeam(response || []);
      } catch (err) {
        console.error("Error cargando equipo", err);
      }
    };
    fetchTeam();
  }, [eventId]);

  useEffect(() => { fetchPromoterLinks(); }, [eventId]);

  const totalGeneratedQrs = promoterLinks.reduce((acc, link) => acc + (link.current_uses || 0), 0);

  console.log(promoterLinks);

  return (
    <div className="w-full max-w-4xl mx-auto pb-20 pt-16 fade-in">
      <div className="bg-black/40 backdrop-blur-md rounded-2xl p-6 border border-white/10 min-h-[500px]">
        <h2 className="text-2xl font-light mb-6 tracking-wider text-center text-white text-uppercase">
          GESTIONAR LINKS
        </h2>

        {/* ESTADÍSTICAS */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl flex items-center justify-between">
                <div>
                    <p className="text-blue-400 text-xs font-bold uppercase tracking-wider">QRs Generados Total</p>
                    <p className="text-2xl font-bold text-white">{totalGeneratedQrs}</p>
                </div>
                <LinkIcon className="text-blue-400 opacity-50" size={24} />
            </div>

            <button
                onClick={() => setShowPromoterLinkModal(true)}
                className="px-6 py-4 bg-white text-black rounded-xl font-bold hover:bg-gray-200 transition-all flex items-center justify-center gap-2 shadow-lg active:scale-95"
            >
                <Plus size={20} />
                Crear Link Personalizado
            </button>
        </div>

        {/* LISTA DE LINKS */}
        <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-300 border-b border-white/10 pb-2">
                {isCreator ? 'Links de Promotor Actuales' : 'Mis Links de Promotor'}
            </h3>

            {promoterLinks.length === 0 ? (
                <div className="py-12 text-center bg-white/5 rounded-xl border border-dashed border-white/10">
                    <LinkIcon className="mx-auto h-10 w-10 text-gray-600 mb-3" />
                    <p className="text-gray-400">No hay links activos.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-3">
                    {promoterLinks.map((link) => (
                        <div 
                            key={link.id}
                            className="flex flex-col md:flex-row md:items-center justify-between bg-black/20 rounded-xl p-4 border border-white/5 hover:border-white/10 transition-all gap-4"
                        >
                            <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white shrink-0 shadow-lg ${link.current_uses >= link.max_uses ? 'bg-red-500/20 text-red-500' : 'bg-blue-600'}`}>
                                    <Tag size={20} />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {/* MODIFICACIÓN: Mostrar el NOMBRE del link con prioridad */}
                                        <p className="font-bold text-white text-lg">
                                            {`Link - ${link.target_name}`}
                                        </p>
                                        <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-md border ${link.current_uses >= link.max_uses ? 'bg-red-500/10 border-red-500/30 text-red-400' : 'bg-white/10 border-white/10 text-gray-400'}`}>
                                            {link.current_uses} / {link.max_uses} USOS
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        ID: #{link.id} • Por: <span className="text-gray-400">@{link.promoter_name || 'Desconocido'}</span>
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-2 self-end md:self-auto">
                                <button
                                    onClick={() => {
                                        const linkUrl = `${window.location.origin}/event/${eventId}?ref=${link.encoded_info}`;
                                        copyToClipboard(linkUrl, link.id);
                                    }}
                                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg transition-all text-sm font-medium ${copiedId === link.id ? 'bg-green-500 text-white' : 'bg-white/5 hover:bg-white/10 text-white'}`}
                                >
                                    {copiedId === link.id ? <><Check size={16}/> Copiado</> : <><Copy size={16} /> Copiar Link</>}
                                </button>
                                <button
                                    onClick={() => setEditingLink(link)}
                                    className="p-2.5 rounded-lg bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 transition-colors"
                                >
                                    <Tag size={18} /> {/* O usa Edit2 si lo importas */}
                                </button>
                                <button
                                    onClick={() => handleDeletePromoterLink(link.id)}
                                    className="p-2.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>

        {/* PAGINACIÓN */}
        {total > limit && (
          <div className="flex justify-center items-center gap-4 mt-8 pt-4 border-t border-white/5">
            <button onClick={handlePrevPage} disabled={page === 0} className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white rounded-lg disabled:opacity-30 text-sm">Anterior</button>
            <span className="text-gray-400 text-sm">{page + 1} / {Math.ceil(total / limit)}</span>
            <button onClick={handleNextPage} disabled={page >= Math.ceil(total / limit) - 1} className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white rounded-lg disabled:opacity-30 text-sm">Siguiente</button>
          </div>
        )}
      </div>

      {/* --- MODAL CREAR LINK (Ajustado con NAME) --- */}
      {showPromoterLinkModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <div className="relative bg-[#111] border border-white/10 rounded-3xl shadow-2xl p-8 max-w-md w-full animate-in zoom-in-95 duration-200">
                <h3 className="text-2xl font-bold mb-2 text-white">Nuevo Link</h3>
                <p className="text-gray-400 text-sm mb-6">Asigna un nombre para identificar este link (ej. el nombre del promotor).</p>
                
                <form onSubmit={handleCreatePromoterLink} className="space-y-5">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 ml-1">
                            Asignar a Miembro del Equipo
                        </label>
                        <select
                            value={newPromoterLink.target_id}
                            onChange={(e) => setNewPromoterLink({...newPromoterLink, target_id: e.target.value})}
                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white appearance-none focus:outline-none focus:border-blue-500"
                            required
                        >
                          <option value="" className="bg-[#111]">Seleccionar persona...</option>
                          {/* Mapeo de los miembros del equipo */}
                          {team.map((member) => (
                            <option key={member.user_id} value={member.user_id} className="bg-[#111]">
                              {member.name}
                            </option>
                          ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 ml-1">
                            Límite de usos
                        </label>
                        <select
                            value={newPromoterLink.max_uses}
                            onChange={(e) => setNewPromoterLink({...newPromoterLink, max_uses: parseInt(e.target.value)})}
                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white appearance-none focus:outline-none focus:border-blue-500 transition-colors"
                        >
                            {[5, 10, 15, 20, 50, 100, 500].map(val => (
                                <option key={val} value={val} className="bg-[#111]">{val} QRs (Límite)</option>
                            ))}
                        </select>
                    </div>

                    <div className="pt-4 flex gap-3">
                        <button
                            type="button"
                            onClick={() => setShowPromoterLinkModal(false)}
                            className="flex-1 py-4 px-4 bg-white/5 text-white rounded-2xl font-bold hover:bg-white/10 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={createLoading || !newPromoterLink.target_id}
                            className="flex-1 py-4 px-4 bg-white text-black rounded-2xl font-bold hover:bg-gray-200 transition-all disabled:opacity-50"
                        >
                            {createLoading ? 'Creando...' : 'Asignar Link'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
      )}

      {/* --- MODAL EDITAR LINK --- */}
      {editingLink && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <div className="relative bg-[#111] border border-white/10 rounded-3xl shadow-2xl p-8 max-w-md w-full animate-in zoom-in-95 duration-200">
                <h3 className="text-2xl font-bold mb-2 text-white">Ajustar Límite</h3>
                <p className="text-gray-400 text-sm mb-6">
                  Modificando link de <strong>{editingLink.target_name}</strong>
                </p>
                
                <form onSubmit={handleUpdateLink} className="space-y-5">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 ml-1">
                            Nuevo límite de usos
                        </label>
                        <select
                            value={editingLink.max_uses}
                            onChange={(e) => setEditingLink({...editingLink, max_uses: parseInt(e.target.value)})}
                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white appearance-none focus:outline-none focus:border-blue-500 transition-colors"
                        >
                            {[5, 10, 15, 20, 50, 100, 500, 1000].map(val => (
                                <option key={val} value={val} className="bg-[#111]">{val} QRs</option>
                            ))}
                        </select>
                        <p className="mt-2 text-[10px] text-gray-500 ml-1">
                          Uso actual: {editingLink.current_uses} QRs
                        </p>
                    </div>

                    <div className="pt-4 flex gap-3">
                        <button
                            type="button"
                            onClick={() => setEditingLink(null)}
                            className="flex-1 py-4 px-4 bg-white/5 text-white rounded-2xl font-bold hover:bg-white/10 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={editLoading}
                            className="flex-1 py-4 px-4 bg-blue-600 text-white rounded-2xl font-bold hover:bg-blue-500 transition-all disabled:opacity-50 shadow-lg shadow-blue-500/20"
                        >
                            {editLoading ? 'Guardando...' : 'Actualizar'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
      )}
    </div>
  );
};

export default EventLinksTab;